import React from 'react';
import {
  AvailableThemeOverridesStrict,
  ThemeProviderProps,
  ThemeProvider,
  VariableColorKeys,
  ThemeContainer,
} from '@dropbox/dig-foundations';
import { HexColor } from 'signer-app/white-labeling/types';
import { useBundleProps } from 'signer-app/bundle-props';

type ColorOverides = {
  [K in VariableColorKeys]?: HexColor;
};

type Props = Omit<ThemeProviderProps, 'overrides'> & {
  colors?: 'reset';
};

/**
 * This is a convenience wrapper around DIG's `ThemeProvider` that allows you to
 * pass color theme overrides without needing to specify both `bright` and `dark`
 * modes. White-labeling only supplies one set of colors.
 */
export function WhiteLabelThemeProvider({ colors, children, ...rest }: Props) {
  // const overrides = colors === 'reset' ? 'reset' : createOverrides(colors);

  const { whiteLabelStyles, hasCustomStyles } = useBundleProps();

  const overrides = React.useMemo(() => {
    let colorOverrides: ColorOverides | undefined;
    if (colors === 'reset') {
      return 'reset';
    } else if (hasCustomStyles) {
      colorOverrides = {
        'Background Subtle': whiteLabelStyles.headerBackgroundColor,
        'Opacity Surface - State 1': whiteLabelStyles.secondaryButtonColorHover,
        'Opacity Surface': whiteLabelStyles.secondaryButtonColor,

        'Primary CTA Base - State 1': whiteLabelStyles.primaryButtonColorHover,
        'Primary CTA Base': whiteLabelStyles.primaryButtonColor,
        'Primary CTA On Base': whiteLabelStyles.primaryButtonTextColor,

        'Selection Base - State 1': whiteLabelStyles.primaryButtonColorHover,
        'Selection Base': whiteLabelStyles.primaryButtonColor,
        'Selection On Base': whiteLabelStyles.primaryButtonTextColor,

        'Text Base': whiteLabelStyles.textColor1,
        'Text Subtle': whiteLabelStyles.textColor2,
      };
    }

    return colorOverrides ? createOverrides(colorOverrides) : undefined;
  }, [colors, hasCustomStyles, whiteLabelStyles]);

  return (
    <ThemeProvider theme="vis2023" {...rest} overrides={overrides}>
      <ThemeContainer>{children}</ThemeContainer>
    </ThemeProvider>
  );
}

function createOverrides(colors: ColorOverides): AvailableThemeOverridesStrict {
  const overrideColors: AvailableThemeOverridesStrict['colors'] = {};

  Object.keys(colors).forEach((key) => {
    const colorKey = key as VariableColorKeys;
    const hexColor = colors[colorKey]!;
    if (!hexColor) {
      return;
    }
    overrideColors[colorKey] = {
      dark: hexColor,
      bright: hexColor,
    };
  });

  return {
    colors: overrideColors,
  };
}
